import { mergeAttributes, Node } from '@tiptap/core'
import { OBSERVATION_PANEL_NODE } from '#src/observation-logic/documentUnits.ts'
import { type AttrDefs, nodeAttributes } from '#src/tiptap/nodes/shared.ts'

// What the node view keeps on the node so stored HTML, PDFs and the server
// can show the capture without reading its Observations.
export interface ObservationPanelSummary {
  title?: string
  total: number | null
  band?: { display: string; severity: string }
  answered: number
  of: number
  // False for a measurement panel, which has no total to show.
  scored?: boolean
  // The answered items in order, as the table shows them.
  rows?: { label: string; answer: string }[]
}

// A rendered element: tag, attributes, children.
type HtmlSpec =
  | string
  | readonly [string, Record<string, string>, ...HtmlSpec[]]

const ATTRIBUTES: AttrDefs = {
  id: { dataAttr: 'data-id' },
  templateId: { dataAttr: 'data-template-id' },
  // The NLM definition of a LOINC panel with no instrument in source, kept
  // so notes made from the node rebuild it without calling NLM.
  definition: { dataAttr: 'data-definition', json: true },
  version: { dataAttr: 'data-version' },
  groupId: { dataAttr: 'data-group-id' },
  panelReference: { dataAttr: 'data-panel-reference' },
  mode: { dataAttr: 'data-mode' },
  frozenReference: { dataAttr: 'data-frozen-reference' },
  showWhen: { dataAttr: 'data-show-when', json: true },
  summary: { dataAttr: 'data-summary', json: true },
  /* The answers of a note whose records are made when it is signed or
   * submitted, by item (`system|code`). */
  answers: { dataAttr: 'data-answers', json: true },
  // Calculated items the clinician set by hand (by item, `system|code`),
  // which are not recalculated.
  derivedOverrides: { dataAttr: 'data-derived-overrides', json: true },
  // Every item that shows needs an answer before the note is signed.
  required: { dataAttr: 'data-required', boolean: true },
}

// Inline markup only: block markup such as a table inside the panel's
// paragraph makes an HTML parser close the paragraph, so content saved as HTML
// would come back with the panel moved onto its own line.
function summaryLines(summary: ObservationPanelSummary | null): HtmlSpec[] {
  if (!summary) {
    return []
  }
  const total =
    summary.total === null
      ? `${summary.answered} of ${summary.of} answered`
      : `${summary.total}${summary.band ? ` · ${summary.band.display}` : ''}`
  const lines: HtmlSpec[] = [
    ...(summary.title ? [['strong', {}, summary.title] as const] : []),
    ...(summary.rows ?? []).map((row) => `${row.label}: ${row.answer}`),
    ...(summary.scored === false
      ? []
      : [['strong', {}, `Total: ${total}`] as const]),
  ]
  return lines.flatMap((line, index) =>
    index === 0 ? [line] : [['br', {}] as const, line]
  )
}

// A validated tool recorded as observations: a panel Observation and its
// members, found by the capture's group id. The rendered HTML lists
// `summary` alone, a line each.
export const ObservationPanelNode = Node.create({
  name: OBSERVATION_PANEL_NODE,
  // Inline, like an observation, so text can sit on its line; selectable so
  // it can be selected with the text around it and made conditional.
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return nodeAttributes(ATTRIBUTES)
  },
  parseHTML() {
    // `div` is how panels were saved before they were inline.
    return [
      { tag: `span[data-type="${this.name}"]` },
      { tag: `div[data-type="${this.name}"]` },
    ]
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-type': this.name,
        contentEditable: 'false',
      }),
      ...summaryLines(node.attrs.summary as ObservationPanelSummary | null),
    ]
  },
})
